import { useTranslation, i18next } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Text,
  RadioCardField,
  NumberField,
  TextField,
  useControlledField,
  DropdownField,
} from '@frontend/design-system';
import { checkIsOtherServiceSelected } from '../helpers';
import { radioCardStyles } from '../styles';

interface Props {
  options: { id: string; name: string }[];
  selectedOption?: string;
  setSelectedOption: (option: string) => void;
  serviceType?: string;
  setServiceType: (type: string) => void;
  duration: number | 'Custom';
  setDuration: (duration: number | 'Custom') => void;
  customDuration?: number;
  setCustomDuration: (duration?: number) => void;
  disableDuration?: boolean;
}

const CUSTOM_DURATION_OPTION = i18next.t('Custom', { ns: 'schedule-onboarding' });
export const DURATION_OPTIONS = [15, 30, 45, 60, 120];
export const DEFAULT_DURATION = 60;

export const AppointmentTypeSelectionStep = ({
  options,
  selectedOption,
  setSelectedOption,
  serviceType,
  setServiceType,
  duration,
  setDuration,
  customDuration,
  setCustomDuration,
  disableDuration,
}: Props) => {
  const { t } = useTranslation('schedule-onboarding');

  const appointmentTypeRadioField = useControlledField({
    type: 'radio',
    value: selectedOption,
    onChange: (value) => {
      // NOTE: in pointer-events: unset case, we receive undefined value when user click on that area
      // So to avoid that to reset radio selection, keeping this empty check
      if (!value) return;
      setSelectedOption(value);
    },
  });
  const serviceTypeField = useControlledField({
    type: 'text',
    value: serviceType,
    onChange: (value) => setServiceType(value),
  });

  const durationField = useControlledField({
    type: 'dropdown',
    required: true,
    value: String(duration || DEFAULT_DURATION),
    onChange: (value) => {
      setDuration(value);
      setCustomDuration(undefined);
    },
  });

  const isCustomDurationSelected = durationField.value === CUSTOM_DURATION_OPTION;

  const customDurationField = useControlledField({
    type: 'number',
    required: isCustomDurationSelected,
    value: String(customDuration ?? ''),
    includeThousandsSeparator: false,
    max: 1440,
    min: 5,
    allowDecimal: false,
    allowNegative: false,
    onChange: (value) => setCustomDuration(value),
  });

  return (
    <section css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      <RadioCardField animate name='appointment-type-radio' {...appointmentTypeRadioField} css={radioCardStyles.field}>
        {options.map((option) => {
          const isOtherOptionSelected = checkIsOtherServiceSelected(option.id, selectedOption);
          return (
            <RadioCardField.Option
              key={option.id + option.name}
              value={option.id || option.name}
              title={option.name}
              css={[radioCardStyles.radioOption, isOtherOptionSelected && radioCardStyles.expandedRadioOption]}
              selectionContent={
                isOtherOptionSelected && (
                  <TextField
                    autoFocus
                    name='service-type'
                    label={t('Service Type')}
                    css={radioCardStyles.input}
                    {...serviceTypeField}
                  />
                )
              }
            />
          );
        })}
      </RadioCardField>

      <Text weight='bold'>{t('How long does it typically take?')}</Text>

      <div css={{ display: 'flex', gap: theme.spacing(2) }}>
        <DropdownField
          name='appointment-duration'
          label={t('Duration')}
          disabled={disableDuration}
          {...durationField}
          css={{ width: 250 }}
        >
          {DURATION_OPTIONS.map((minutes) => (
            <DropdownField.Option key={minutes} value={String(minutes)}>
              {minutes} {t('Mins')}
            </DropdownField.Option>
          ))}
          <DropdownField.Option value={CUSTOM_DURATION_OPTION}>{CUSTOM_DURATION_OPTION}</DropdownField.Option>
        </DropdownField>

        {isCustomDurationSelected && (
          <NumberField
            name='custom-duration'
            label={t('Minutes')}
            disabled={disableDuration}
            {...customDurationField}
          />
        )}
      </div>
    </section>
  );
};
